// ABMSocialCommentsPlaybook.js
// Coursera-branded PDF/playbook: "Social Comments → Structured Contacts"

import React from "react";

const TOKENS = {
  blue: "#2A73CC",
  navy: "#0E2B57",
  ink: "#0B1A3C",
  ink70: "#0B1A3CB3",
  line: "#E6EDF2",
  bg: "#F4F6FA",
  card: "#FFFFFF",
  darkBand: "#0F2A57",
};

const LOGO_C_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_C_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";

// Content thumbnail used on the cover
const THUMB_TOOL =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/sdrs_clipboard.png";

// Where the “Back to hub” button should go.
// If you list this asset under SDR, change to "#sdr".
const BACK_TO = "#marketing";

/* --------------------- Small, inline PDF header (compact only) --------------------- */
function PdfHeader({ backHref = BACK_TO }) {
  const wrap = {
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "#ffffffcc",
    backdropFilter: "saturate(180%) blur(6px)",
    borderBottom: `1px solid ${TOKENS.line}`,
  };
  const inr = {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  };
  const btn = {
    fontWeight: 700,
    borderRadius: 10,
    padding: "8px 12px",
    border: `1px solid ${TOKENS.line}`,
    cursor: "pointer",
    textDecoration: "none",
    color: TOKENS.ink,
    background: "#fff",
  };
  const pri = {
    ...btn,
    background: TOKENS.blue,
    color: "#fff",
    borderColor: "transparent",
  };
  return (
    <header style={wrap} className="pdf-top">
      <div style={inr}>
        <a
          className="brand"
          href={backHref}
          aria-label="Back to hub"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
          }}
        >
          <picture>
            <source srcSet={LOGO_C_SVG} type="image/svg+xml" />
            <img src={LOGO_C_PNG} alt="Coursera" height="18" />
          </picture>
          <span style={{ color: "#3F4D67", fontWeight: 700 }}>
            for Business
          </span>
        </a>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <a href={backHref} style={btn}>
            Back to hub
          </a>
          <button style={pri} onClick={() => window.print()}>
            Download PDF
          </button>
        </div>
      </div>
    </header>
  );
}

/* ------------------------------ Shared wrappers ------------------------------ */
function Page({ children, n }) {
  return (
    <section
      style={{
        background: "#fff",
        border: `1px solid ${TOKENS.line}`,
        borderRadius: 16,
        padding: 24,
        boxShadow: "0 10px 30px rgba(10,22,50,.10)",
        margin: "24px auto",
        width: "min(1120px, 96vw)",
        position: "relative",
      }}
      aria-label={`Page ${n}`}
    >
      {children}
      <div
        style={{
          position: "absolute",
          right: 16,
          bottom: 10,
          color: "#7B8AA0",
          fontSize: 12,
          userSelect: "none",
        }}
        aria-hidden
      >
        {n}
      </div>
    </section>
  );
}

function Card({ title, kicker, children }) {
  return (
    <div
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.line}`,
        borderRadius: 16,
        padding: 18,
      }}
    >
      {kicker && (
        <div
          style={{
            fontSize: 11,
            letterSpacing: ".04em",
            textTransform: "uppercase",
            fontWeight: 800,
            color: "#6B7C93",
            marginBottom: 6,
          }}
        >
          {kicker}
        </div>
      )}
      {title && (
        <h3
          style={{
            margin: "0 0 8px",
            fontSize: 20,
            lineHeight: "26px",
            color: TOKENS.ink,
          }}
        >
          {title}
        </h3>
      )}
      <div style={{ color: "#102A43" }}>{children}</div>
    </div>
  );
}

function Band({ title, children }) {
  return (
    <div
      style={{
        background: TOKENS.darkBand,
        color: "#EAF2FF",
        borderRadius: 14,
        padding: "12px 14px",
        fontWeight: 800,
        marginBottom: 10,
      }}
    >
      {title}
      {children && <div style={{ fontWeight: 600 }}>{children}</div>}
    </div>
  );
}

/* --------------------------------- HERO (p1) -------------------------------- */
function P1_Hero() {
  return (
    <Page n={1}>
      <div
        style={{
          background: "linear-gradient(180deg, #2A73CC 0%, #1f62b2 100%)",
          color: "#fff",
          borderRadius: 18,
          padding: 20,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Brand row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <picture>
            <source srcSet={LOGO_C_SVG} type="image/svg+xml" />
            <img src={LOGO_C_PNG} alt="Coursera" height="18" />
          </picture>
          <span style={{ opacity: 0.9, fontWeight: 700 }}>for Business</span>
        </div>

        {/* Two-column cover */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr .8fr",
            gap: 20,
            alignItems: "center",
            marginTop: 16,
          }}
        >
          <div>
            <h1
              style={{
                margin: "6px 0 6px",
                fontSize: 38,
                lineHeight: "44px",
                fontWeight: 800,
              }}
            >
              Social Comments → Structured Contacts
            </h1>
            <div
              style={{
                fontSize: 16,
                opacity: 0.95,
                marginBottom: 12,
                fontWeight: 600,
              }}
            >
              A simple, privacy-first helper for ABM teams at Coursera
            </div>

            <div
              style={{
                background: "rgba(255,255,255,.14)",
                border: "1px solid rgba(255,255,255,.35)",
                borderRadius: 12,
                padding: 14,
                fontSize: 15,
                lineHeight: "22px",
              }}
            >
              Paste reaction blocks from social posts and get a clean, deduped
              contacts table with names, roles, companies, connection degree,
              and (when present) profile links—ready for CSV/XLSX export. No
              crawling. No automation. Clipboard-only, in the browser.
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 8,
              boxShadow: "0 12px 40px rgba(10,22,50,.25)",
            }}
            aria-hidden
          >
            <img
              src={THUMB_TOOL}
              alt=""
              style={{ display: "block", width: "100%", borderRadius: 12 }}
            />
          </div>
        </div>
      </div>
    </Page>
  );
}

/* ------------------------ Page 2: Value / Where it fits --------------------- */
function P2_Value() {
  return (
    <Page n={2}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card kicker="Why this exists" title="What this tool gives ABM">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              <b>Fast list-building from public reactions</b> — transform
              comments/reactions you already see into structured contacts.
            </li>
            <li>
              <b>Smarter hand-offs</b> — export CSV/XLSX with clean headers for
              SDRs, AEs, or enrichment tools.
            </li>
            <li>
              <b>PII-safe defaults</b> — emails are optional/blank; keep it
              privacy-first and platform-friendly.
            </li>
            <li>
              <b>Handle & URL capture</b> — when profile links are present, we
              detect and pass them through.
            </li>
            <li>
              <b>Connection degree</b> — auto-capture 1st/2nd/3rd+ when visible.
            </li>
          </ul>
        </Card>

        <Card
          kicker="Where it shines"
          title="Use it on ABM 1:1 & 1:Few accounts"
        >
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              <b>Net-new contacts</b> from priority accounts reacting to
              learning/skills topics.
            </li>
            <li>
              <b>Buying-committee discovery</b> — reveal titles/functions you’re
              missing in the plan.
            </li>
            <li>
              <b>Signal-driven sequences</b> — pass role + context to SDRs for
              tighter outreach.
            </li>
            <li>
              <b>Short working sessions</b> — copy/paste, convert, export in a
              few minutes with your AE/SDR.
            </li>
          </ul>
        </Card>
      </div>

      <div style={{ height: 14 }} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card kicker="What we parse" title="Fields captured">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Full name (+ first / last)</li>
            <li>Title / Role (with simple company detection)</li>
            <li>Company (best-effort heuristic)</li>
            <li>Connection degree (1st / 2nd / 3rd+ when shown)</li>
            <li>Profile handle & URL (when a link is present)</li>
          </ul>
        </Card>

        <Card kicker="Guardrails" title="What this tool does NOT do">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>No bots, crawlers, or page automation</li>
            <li>No server-side scraping or hidden network calls</li>
            <li>No persistence: data lives in your browser until you export</li>
            <li>No credential capture or platform API usage</li>
          </ul>
        </Card>
      </div>
    </Page>
  );
}

/* -------------------------- Page 3: Workflow / Rhythm ----------------------- */
function P3_Workflow() {
  return (
    <Page n={3}>
      <Band title="How it works — clipboard only, local processing" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <Card title="1) Copy">
          Open a post’s reactions/comments. Select visible text you are
          authorized to view. <b>Copy</b>.
        </Card>
        <Card title="2) Paste">
          In the tool, paste into the box. We’ll include any profile links your
          clipboard provides.
        </Card>
        <Card title="3) Convert">
          Click <b>Convert</b>. We structure contacts, dedupe them, and show a
          clean table for review.
        </Card>
        <Card title="4) Export">
          Export CSV/XLSX with clean headers. Send to SDRs, AEs, or enrichment
          (e.g., ZeroBounce/Hunter).
        </Card>
      </div>

      <Band title="Team rhythm — ABM × SDR × AE" />
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}
      >
        <Card title="ABM">
          Prioritize posts/accounts, align markers (like/love/etc.), and
          validate buying-committee coverage.
        </Card>
        <Card title="SDR">
          Use exports to multi-thread sequences with the mapped roles. Add new
          signal back into the plan.
        </Card>
        <Card title="AE">
          Decide which contacts matter most, set the next two plays, and track
          outcomes in the account plan.
        </Card>
      </div>
    </Page>
  );
}

/* -------------------------- Page 4: Privacy & Fit --------------------------- */
function P4_Privacy() {
  return (
    <Page n={4}>
      <Band title="Privacy-first by design" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Card title="Local-only processing">
          Everything runs in your browser. No server round-trips. No third-party
          beacons. Exports are generated in memory.
        </Card>
        <Card title="User-initiated clipboard">
          The tool only processes what the user copies and pastes—content they
          are allowed to view. No automated fetching.
        </Card>
        <Card title="Data minimization">
          We keep to essential fields (name, title, company, degree, profile
          link when present). Emails are optional/blank by default.
        </Card>
        <Card title="Transparent operations">
          No crawling, no plug-ins, no browser automation. The workflow is
          visible, explainable, and easy for compliance to review.
        </Card>
      </div>

      <Band title="Where this fits in ABM enablement" />
      <Card>
        Use this tool to enrich the buying-committee view for ABM 1:1 and 1:Few
        programs. It’s a fast way to turn public engagement into structured,
        team-ready contacts—without inviting platform risk. Think of it as a
        formatting/enrichment helper for content you already have access to,
        rather than a data-collection engine.
      </Card>
    </Page>
  );
}

/* ----------------------------- Page 5: Tips & Schema ------------------------ */
function P5_Tips() {
  return (
    <Page n={5}>
      <Band title="Tips for higher-quality output" />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Card title="Paste reactions, not entire pages">
          Copy the section with names/titles (and links if visible). Avoid extra
          narrative text to reduce noise.
        </Card>
        <Card title="Keep markers clean">
          The default set (
          <code>like, love, celebrate, insightful, support</code>) is
          case-insensitive. Align this with your capture source for best
          results.
        </Card>
        <Card title="Use enrichment downstream">
          Exported rows play nicely with email verification and domain
          enrichment workflows.
        </Card>
        <Card title="Review before export">
          It takes seconds—spot obvious duplicates or odd titles before handing
          to SDRs.
        </Card>
      </div>

      <Band title="Export schema (CSV/XLSX)" />
      <Card>
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              border: `1px solid ${TOKENS.line}`,
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <thead>
              <tr style={{ background: "#F8FAFE" }}>
                {[
                  "full_name",
                  "first_name",
                  "last_name",
                  "title_role",
                  "company",
                  "connection",
                  "profile_handle",
                  "profile_url",
                  "email",
                  "domain",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "10px 12px",
                      borderBottom: `1px solid ${TOKENS.line}`,
                      fontWeight: 700,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "10px 12px" }}>Jane Smith</td>
                <td style={{ padding: "10px 12px" }}>Jane</td>
                <td style={{ padding: "10px 12px" }}>Smith</td>
                <td style={{ padding: "10px 12px" }}>VP, Revenue Operations</td>
                <td style={{ padding: "10px 12px" }}>Acme Technologies</td>
                <td style={{ padding: "10px 12px" }}>2nd</td>
                <td style={{ padding: "10px 12px" }}>jane-smith-1234</td>
                <td style={{ padding: "10px 12px" }}>
                  https://www.linkedin.com/in/jane-smith-1234/
                </td>
                <td style={{ padding: "10px 12px" }}>(blank)</td>
                <td style={{ padding: "10px 12px" }}>(optional)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </Page>
  );
}

/* --------------------------------- Assemble -------------------------------- */
export default function ABMSocialCommentsPlaybook({ compact = false }) {
  const pages = [
    <P1_Hero key="p1" />,
    <P2_Value key="p2" />,
    <P3_Workflow key="p3" />,
    <P4_Privacy key="p4" />,
    <P5_Tips key="p5" />,
  ];

  // In compact mode (rendered via #/pdf/*), include the local header for nav/print
  if (compact) {
    return (
      <div style={{ background: TOKENS.bg, minHeight: "100vh" }}>
        <PdfHeader backHref={BACK_TO} />
        <div style={{ padding: "12px 0 24px" }}>{pages}</div>
      </div>
    );
  }

  // Embedded in the hub (no extra header)
  return (
    <div
      style={{
        background: TOKENS.bg,
        minHeight: "100vh",
        padding: "16px 0 28px",
      }}
    >
      {pages}
    </div>
  );
}
